import { Component } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { SavedView } from 'app/core/models';
import { AccountService } from 'app/core';
import UrlUtils from 'app/shared/Utils/url.utils';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';
import { SavedViewsSidebarService } from 'app/shared/services/saved-views-sidebar.service';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';

@Component({
  selector: 'custom-objects',
  templateUrl: './custom-objects.component.html',
  styles: [
    `
      :host {
        position: unset !important;
        display: unset !important;
        flex: unset !important;
        height: 100%;
        overflow: hidden;
      }
    `,
  ],
})
export class CustomObjectsComponent extends OnDestroyMixin {
  private savedViews: SavedView[];
  public data: Data;
  public savedViewsSidebarConfig: TsPageSidenavConfig;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly accountService: AccountService,
    private readonly savedViewsSidebarService: SavedViewsSidebarService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data) => {
      this.data = data;
      this.savedViews = data.savedViews.filter(
        (ov: SavedView) =>
          ov.related_to_type == data.customObject.name &&
          (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))
            ? ov.permissions.is_viewable
            : true),
      );

      if (this.savedViews.length === 0) {
        this.createSavedViewAndRedirect();
      } else {
        this.checkRedirectToFirstObjectView(this.activatedRoute);
      }

      this.savedViewsSidebarConfig = this.savedViewsSidebarService.getSidenavConfig(this.data.customObject.name);
    });

    this.router.events.pipe(untilComponentDestroyed(this)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.checkRedirectToFirstObjectView(this.activatedRoute);
      }
    });
  }

  private checkRedirectToFirstObjectView(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && this.savedViews.length > 0) {
      let savedViewId = UrlUtils.getFromLocalStorage(`customObjectSavedView-${this.data.customObject.id}`);
      if (savedViewId && this.isSavedViewPresent(+savedViewId)) {
        this.router.navigate(['./', savedViewId, 'table'], { relativeTo: route, queryParamsHandling: 'merge' });
      } else {
        this.router.navigate(['./', this.savedViews[0].id, 'table'], {
          relativeTo: route,
          queryParamsHandling: 'merge',
        });
      }
    }
  }

  private isSavedViewPresent(savedViewId: number): boolean {
    return !!this.savedViews.find((x) => x.id === savedViewId);
  }

  private createSavedViewAndRedirect(): void {
    const newSavedView = {
      id: null,
      name: `All ${this.data.customObject.label_plural}`,
      related_to_type: this.data.customObject.name,
    };
    this.accountService
      .createSavedView(newSavedView)
      .pipe(untilComponentDestroyed(this))
      .subscribe((savedView) => {
        this.savedViews = [savedView];
        this.router.navigate(['./', savedView.id, 'table'], {
          relativeTo: this.activatedRoute,
          queryParamsHandling: 'merge',
        });
      });
  }
}
